import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import useAuth from "../../auth/useAuth";

const API_BASE_URL       = process.env.REACT_APP_API_URL;
const MAX_ATTEMPTS       = 5;
const LOCKOUT_MS         = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 15000;

// ─── Lockout Service (UNCHANGED) ───────────────────────────────
const LockoutService = {
  _key: "login_lockout",
  get() {
    try {
      return JSON.parse(sessionStorage.getItem(this._key)) ?? { attempts: 0, until: null };
    } catch {
      return { attempts: 0, until: null };
    }
  },
  set(data) {
    sessionStorage.setItem(this._key, JSON.stringify(data));
  },
  recordFailure() {
    const s = this.get();
    const attempts = s.attempts + 1;
    const until = attempts >= MAX_ATTEMPTS ? Date.now() + LOCKOUT_MS : s.until;
    this.set({ attempts, until });
    return { attempts, until };
  },
  reset() {
    sessionStorage.removeItem(this._key);
  },
};

// ─── Axios ─────────────────────────────────────────────────────
const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

// ─── Error Handler (UNCHANGED) ─────────────────────────────────
function parseApiError(err) {
  if (err.code === "ECONNABORTED")
    return "Request timed out. Please check your connection.";
  if (!err.response)
    return "Unable to reach the server. Check your network connection.";

  const { status, data } = err.response;

  if (data?.detail) return data.detail;

  const firstField = Object.values(data ?? {})[0];
  if (Array.isArray(firstField)) return firstField[0];

  switch (status) {
    case 400: return "Invalid request. Please check your input.";
    case 401: return "Incorrect email or password.";
    case 403: return "Access denied. Your account may be suspended.";
    case 429: return "Too many requests. Please wait a moment.";
    case 500:
    case 502:
    case 503: return "A server error occurred. Please try again later.";
    default:  return `Unexpected error (${status}). Please try again.`;
  }
}

// ─── Countdown Hook ────────────────────────────────────────────
function useCountdown(targetMs) {
  const [remaining, setRemaining] = useState(() =>
    targetMs ? Math.max(0, targetMs - Date.now()) : 0
  );

  useEffect(() => {
    if (!targetMs) return;
    const tick = () => setRemaining(Math.max(0, targetMs - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  return remaining;
}

// ─── Icons (UNCHANGED) ─────────────────────────────────────────
const EyeIcon = () => (/* same svg */ <svg width="16" height="16"><circle cx="8" cy="8" r="7"/></svg>);
const EyeOffIcon = () => (<svg width="16" height="16"></svg>);
const LockIcon = () => (<svg width="13" height="13"></svg>);
const SpinIcon = () => (<svg className="animate-spin" width="15" height="15"></svg>);
const AlertCircleIcon = () => (<svg width="15" height="15"></svg>);
const CheckCircleIcon = () => (<svg width="15" height="15"></svg>);
const ClockIcon = () => (<svg width="15" height="15"></svg>);

// ─── Component ─────────────────────────────────────────────────
const Login = () => {
  const { login } = useAuth();

  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [showPwd, setShowPwd]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [lockoutUntil, setLockoutUntil] = useState(
    () => LockoutService.get().until ?? null
  );

  const emailRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from     = location.state?.from?.pathname || "/dashboard";

  const remainingMs  = useCountdown(lockoutUntil);
  const isLocked     = remainingMs > 0;
  const remainingSec = Math.ceil(remainingMs / 1000);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isLocked) return;

      const trimmedEmail = email.trim();
      const emailRegex   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!trimmedEmail || !password) {
        setError("Email and password are required.");
        return;
      }

      if (!emailRegex.test(trimmedEmail)) {
        setError("Enter a valid email address.");
        return;
      }

      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }

      try {
        setLoading(true);
        setError("");
        setSuccessMsg("");

        const { data } = await authApi.post("/api/superadmin/login/", {
          email: trimmedEmail,
          password,
        });

        const access  = data.access_token  || data.access;
        const refresh = data.refresh_token || data.refresh;

        if (!access || !refresh) {
          throw new Error("Invalid response");
        }

        // ✅ MAIN CHANGE (context)
        login(access, refresh, trimmedEmail);

        LockoutService.reset();

        setSuccessMsg("Login successful. Redirecting…");
        setTimeout(() => navigate(from, { replace: true }), 700);

      } catch (err) {
        const message = parseApiError(err);
        const { attempts, until } = LockoutService.recordFailure();

        if (until) {
          setLockoutUntil(until);
          setError(
            `Too many failed attempts. Locked for ${Math.ceil(LOCKOUT_MS / 60000)} minutes.`
          );
        } else {
          const attemptsLeft = MAX_ATTEMPTS - attempts;
          setError(
            `${message}${
              attemptsLeft <= 2
                ? ` (${attemptsLeft} attempts remaining)`
                : ""
            }`
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [email, password, isLocked, from, navigate, login]
  );

  const inputCls = (hasError) =>
    `w-full rounded-lg border px-3 py-2 text-sm ${
      hasError ? "border-red-300" : "border-gray-300"
    }`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white p-6 rounded-xl shadow">

        <h1 className="text-xl font-semibold text-center mb-4">
          Admin Console
        </h1>

        {isLocked && (
          <div className="text-yellow-600 text-sm mb-3 flex gap-2">
            <ClockIcon /> Locked. Try in {remainingSec}s
          </div>
        )}

        {error && (
          <div className="text-red-600 text-sm mb-3 flex gap-2">
            <AlertCircleIcon /> {error}
          </div>
        )}

        {successMsg && (
          <div className="text-green-600 text-sm mb-3 flex gap-2">
            <CheckCircleIcon /> {successMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          <input
            ref={emailRef}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || isLocked}
            className={inputCls(!!error)}
          />

          <div className="relative">
            <input
              type={showPwd ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading || isLocked}
              className={`${inputCls(!!error)} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPwd(!showPwd)}
              className="absolute right-2 top-2"
            >
              {showPwd ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading || isLocked}
            className="w-full bg-indigo-600 text-white py-2 rounded"
          >
            {loading
              ? <><SpinIcon /> Authenticating…</>
              : isLocked
              ? `Locked ${remainingSec}s`
              : "Sign In"}
          </button>

        </form>

        <div className="text-xs text-gray-400 mt-4 flex justify-center gap-1">
          <LockIcon /> Encrypted · Admin access only
        </div>

      </div>
    </div>
  );
};

export default Login;